import { createError, ERRORS } from './protocol'

const isIndex = segment => /^\d+$/.test(segment)

function splitRef(ref) {
  const text = String(ref).trim()
  const [head, ...rest] = text.split('/')

  if (head.startsWith('$') && head.includes('.')) {
    const [alias, ...linked] = head.split('.')
    return [alias, ...linked, ...rest]
  }

  return [head, ...rest]
}

export function createRefResolver({ query, aliases }) {
  const nodes = () => query.getNodes()
  const exists = id => Boolean(nodes()[id])
  const data = id => nodes()[id]?.data

  const pathOf = id => {
    const parts = []
    let current = id

    while (current && current !== 'ROOT') {
      const nodeId = current
      const parentId = data(nodeId)?.parent
      const parent = parentId ? data(parentId) : null
      if (!parent) {
        break
      }

      const linkedName = Object.keys(parent.linkedNodes || {}).find(name => parent.linkedNodes[name] === nodeId)
      parts.unshift(linkedName || String(parent.nodes.indexOf(nodeId)))
      current = parentId
    }

    return ['ROOT', ...parts].join('/')
  }

  const walk = (startId, visit) => {
    const stack = [startId]

    while (stack.length) {
      const id = stack.pop()
      const node = data(id)
      if (!node) {
        continue
      }

      visit(id, node)

      const linked = Object.keys(node.linkedNodes || {}).sort().map(name => node.linkedNodes[name])
      const next = [...linked, ...node.nodes || []]
      for (let i = next.length - 1; i >= 0; i--) {
        stack.push(next[i])
      }
    }
  }

  const byName = name => {
    const found = []
    walk('ROOT', (id, node) => {
      if (node.custom?.name === name) {
        found.push(id)
      }
    })

    if (!found.length) {
      throw createError(ERRORS.NODE_NOT_FOUND, `No node is named "${name}"`, { name })
    }

    if (found.length > 1) {
      throw createError(ERRORS.AMBIGUOUS_REF, `${found.length} nodes are named "${name}"`, {
        name,
        candidates: found.map(id => ({ id, path: pathOf(id) })),
      })
    }

    return found[0]
  }

  const resolveBase = base => {
    if (base === 'ROOT') {
      return 'ROOT'
    }

    if (base.startsWith('$')) {
      const alias = base.slice(1)
      const id = aliases.get(alias)

      if (!id) {
        throw createError(ERRORS.NODE_NOT_FOUND, `Unknown alias "$${alias}"`, { alias, aliases: [...aliases.keys()] })
      }

      if (!exists(id)) {
        aliases.delete(alias)
        throw createError(ERRORS.NODE_NOT_FOUND, `Alias "$${alias}" points to a node that no longer exists`, { alias, id })
      }

      return id
    }

    if (base.startsWith('name:')) {
      return byName(base.slice(5))
    }

    if (exists(base)) {
      return base
    }

    throw createError(ERRORS.NODE_NOT_FOUND, `Node "${base}" not found`, { ref: base })
  }

  const resolveSegment = (id, segment) => {
    const node = data(id)
    const linkedNames = Object.keys(node.linkedNodes || {})

    if (isIndex(segment)) {
      const childId = node.nodes[Number(segment)]
      if (!childId) {
        throw createError(ERRORS.NODE_NOT_FOUND, `"${pathOf(id)}" has no child at index ${segment}`, {
          id,
          children: node.nodes.length,
          linkedNodes: linkedNames,
        })
      }
      return childId
    }

    const linkedId = node.linkedNodes?.[segment]
    if (!linkedId) {
      throw createError(ERRORS.NODE_NOT_FOUND, `"${pathOf(id)}" has no linked node "${segment}"`, {
        id,
        linkedNodes: linkedNames,
        children: node.nodes.length,
      })
    }

    return linkedId
  }

  const resolve = ref => {
    if (ref === undefined || ref === null || ref === '') {
      throw createError(ERRORS.INVALID_OP, 'Missing node reference')
    }

    const [base, ...segments] = splitRef(ref)
    return segments.reduce(resolveSegment, resolveBase(base))
  }

  const match = ({ block, name, within } = {}) => {
    if (!block && !name) {
      throw createError(ERRORS.INVALID_OP, 'match needs block and/or name')
    }

    const start = within ? resolve(within) : 'ROOT'
    const found = []

    walk(start, (id, node) => {
      if (id === 'ROOT') {
        return
      }
      if (block && node.name !== block) {
        return
      }
      if (name && node.custom?.name !== name) {
        return
      }
      found.push(id)
    })

    if (!found.length) {
      throw createError(ERRORS.NODE_NOT_FOUND, 'No node matches the given criteria', { match: { block, name, within } })
    }

    return found
  }

  const targets = op => {
    if (op.node !== undefined && op.node !== null) {
      return [resolve(op.node)]
    }

    if (op.match) {
      return match(op.match)
    }

    throw createError(ERRORS.INVALID_OP, `"${op.op}" needs a node reference or a match`)
  }

  const forgetNode = id => {
    for (const [alias, target] of [...aliases.entries()]) {
      if (target === id) {
        aliases.delete(alias)
      }
    }
  }

  const isDescendant = (id, ancestorId) => {
    if (id === ancestorId) {
      return true
    }

    let current = data(id)?.parent
    while (current) {
      if (current === ancestorId) {
        return true
      }
      current = data(current)?.parent
    }

    return false
  }

  return { resolve, match, targets, exists, pathOf, walk, forgetNode, isDescendant }
}
